import { useCallback, useEffect, useState } from 'react';
import { useToast, toastFromError } from '../../components/ToastProvider';
import {
  getDiscoveryStatus, createWorkItem, updateWorkItem, listCompetitorRegistry,
  type DiscoveryStats, type DiscoveryWorkItem, type CompetitorRegistryEntry,
  type NewWorkItemRequest,
} from '../../lib/growth-api';
import { currentTimeZone, formatInstant } from '../../lib/time-format';

/**
 * Ops → Growth → Discovery (ADR-121, D-010).
 *
 * ⭐⭐ THIS SCREEN WATCHES A SERVICE, IT DOES NOT DRIVE ONE — and that is the difference
 * between G0 and everything else on this surface. Harvest runs when an operator presses a
 * button; G0 runs on a schedule whether or not anyone is here, working a queue of briefs
 * and falling back to its own standing directives so the index keeps growing between
 * campaigns. So there is no "Run now": a manual trigger would be a second execution path
 * with its own concurrency question against a worker that assumes it is alone with the
 * queue. What an operator does here is QUEUE work and PAUSE work.
 *
 * ⛔ AND IT IS NOT A PIPELINE UI. D-011 walked "not a CRM" back to "not an EXTERNAL CRM"
 * and drew the line at integration and interface: G0's record is read by algorithms and
 * dashboards, not worked as leads by a person. So this shows counters, a queue, a standing
 * list and the exclusion registry — and deliberately lists no contacts and opens no
 * business record for editing.
 *
 * ⚠ THE PLACES BANNER IS THE MOST IMPORTANT THING ON THE PAGE RIGHT NOW. Measured 19
 * September 2026: the Google Places key is real and the Google Cloud project has no Maps
 * API enabled, so the primary geo tier returns 403 on every call and the waterfall runs on
 * its site-scrape fallback. Discovery works — the Census geocoder is free and keyless — but
 * at `medium` confidence instead of `high`, and nothing except this banner would say so.
 */
const PLACES_LABELS: Record<string, { tone: 'warn' | 'ok' | 'muted'; text: string }> = {
  not_enabled: {
    tone: 'warn',
    text: 'Google Places is not enabled on its Google Cloud project — every lookup is refused. '
      + 'Geography is still resolved from each site’s own published address, at lower confidence. '
      + 'Enabling Places API (New) in the Google console restores the primary tier.',
  },
  not_provisioned: {
    tone: 'warn',
    text: 'No Google Places key is stored. Geography is resolved from each site’s own published address.',
  },
  no_match: { tone: 'ok', text: 'Google Places answered — the last business had no matching profile.' },
  matched: { tone: 'ok', text: 'Google Places matched the last business resolved.' },
  error: { tone: 'warn', text: 'Google Places failed on the last run. Transient — the next run retries.' },
  skipped: { tone: 'muted', text: 'The last run’s crawl budget was spent before a Places lookup.' },
};

export function GrowthDiscoveryTab() {
  const toast = useToast();
  const [stats, setStats] = useState<DiscoveryStats | null>(null);
  const [briefs, setBriefs] = useState<DiscoveryWorkItem[]>([]);
  const [directives, setDirectives] = useState<DiscoveryWorkItem[]>([]);
  const [competitors, setCompetitors] = useState<CompetitorRegistryEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<NewWorkItemRequest>({
    kind: 'directive', countryCode: 'US', regionCode: 'TX', sector: '', targetCount: 50,
  });

  const load = useCallback(async () => {
    try {
      const res = await getDiscoveryStatus();
      setStats(res.stats);
      setBriefs(res.briefs);
      setDirectives(res.directives);
      setLoadError(null);
    } catch (err) {
      // A failed LOAD is page state, not a toast: a dismissed toast leaves an empty page
      // with no explanation of why.
      setLoadError('Could not load discovery status.');
      const t = toastFromError(err, 'Could not load discovery status');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const submit = async () => {
    if (!form.sector.trim()) return;
    setBusy('create');
    try {
      await createWorkItem({ ...form, sector: form.sector.trim() });
      toast.push({
        variant: 'success',
        title: form.kind === 'brief' ? 'Brief queued' : 'Directive added',
        detail: form.kind === 'brief'
          ? 'It will be worked before every standing directive.'
          : 'It will be worked whenever no brief is queued.',
      });
      setForm((f) => ({ ...f, sector: '' }));
      await load();
    } catch (err) {
      const t = toastFromError(err, 'Could not queue that work');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (item: DiscoveryWorkItem) => {
    setBusy(item.itemId);
    try {
      await updateWorkItem(item.itemId, item.kind === 'directive'
        ? { enabled: !item.enabled }
        : { status: 'cancelled' });
      await load();
    } catch (err) {
      const t = toastFromError(err, 'Could not update that item');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setBusy(null);
    }
  };

  const loadCompetitors = async () => {
    setBusy('competitors');
    try {
      setCompetitors((await listCompetitorRegistry('competitor')).entries);
    } catch (err) {
      const t = toastFromError(err, 'Could not load the exclusion registry');
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
    } finally {
      setBusy(null);
    }
  };

  const places = stats?.lastPlacesStatus ? PLACES_LABELS[stats.lastPlacesStatus] : undefined;

  return (
    <section className="growth-discovery" aria-label="Business discovery">
      <p className="growth-page-intro">
        Discovery builds Nexanomics’ own index of businesses, resolved to a{' '}
        <strong>verified county</strong> rather than an inferred one — a <code>.com</code>{' '}
        carries no geography, and ranking for a “Texas plumber” search does not put a
        business in Texas. It runs continuously in the background: briefs are worked first,
        then the standing list.
      </p>

      {loadError && <p className="growth-field-warning" role="alert">{loadError}</p>}

      {stats === null && !loadError && (
        <div className="growth-connections-loading">
          <div className="growth-skeleton-row" aria-hidden="true" />
          <div className="growth-skeleton-row" aria-hidden="true" />
          <p className="growth-field-hint">Loading discovery status…</p>
        </div>
      )}

      {stats && (
        <>
          {places && (
            <p
              className={places.tone === 'warn' ? 'growth-field-warning' : 'growth-field-hint'}
              role={places.tone === 'warn' ? 'status' : undefined}
            >
              {places.text}
            </p>
          )}

          <div className="growth-stat-row">
            <Stat label="Businesses indexed" value={stats.businessesIndexed} />
            <Stat label="County resolved" value={stats.geoResolved} />
            <Stat label="Already known" value={stats.alreadyKnown} hint="re-found, no vendor spend" />
            <Stat label="Excluded" value={stats.competitorsFlagged} hint="competitors and data brokers" />
            <Stat label="Runs" value={stats.runs} />
          </div>
          <p className="growth-field-hint">
            {stats.lastRunAt
              ? `Last run ${formatInstant(stats.lastRunAt, currentTimeZone()).text}.`
              : 'No run has completed yet.'}{' '}
            Discovery runs every 15 minutes.
          </p>
        </>
      )}

      <div className="growth-step">
        <h3 className="growth-step-title">Queue work</h3>
        <p className="growth-step-help">
          A <strong>brief</strong> is a one-off request — a geography, an industry, a number
          of businesses — and is worked ahead of everything else. A{' '}
          <strong>directive</strong> is a standing instruction worked whenever no brief is
          queued, so the index keeps growing between campaigns.
        </p>

        <div className="growth-form">
          <label className="growth-field growth-field-narrow">
            <span className="growth-field-label">Kind</span>
            <select
              value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as NewWorkItemRequest['kind'] }))}
            >
              <option value="directive">Standing directive</option>
              <option value="brief">One-off brief</option>
            </select>
          </label>

          <label className="growth-field growth-field-narrow">
            <span className="growth-field-label">Country</span>
            <select
              value={form.countryCode}
              onChange={(e) => setForm((f) => ({
                ...f,
                countryCode: e.target.value as NewWorkItemRequest['countryCode'],
                // ⚠ County FIPS is US-only, and clearing it on a country change is not
                // cosmetic: the server refuses the pair, and leaving a stale value in a
                // hidden field would fail a submission the operator cannot see the cause of.
                countyFips: e.target.value === 'US' ? f.countyFips : undefined,
              }))}
            >
              <option value="US">United States</option>
              <option value="AU">Australia</option>
              <option value="NZ">New Zealand</option>
            </select>
          </label>

          <label className="growth-field growth-field-narrow">
            <span className="growth-field-label">State / region</span>
            <input
              value={form.regionCode}
              onChange={(e) => setForm((f) => ({ ...f, regionCode: e.target.value.toUpperCase() }))}
              maxLength={8}
              aria-label="State or region code"
            />
          </label>

          {form.countryCode === 'US' && (
            <label className="growth-field growth-field-narrow">
              <span className="growth-field-label">
                County FIPS <span className="growth-field-optional">(optional)</span>
              </span>
              <input
                value={form.countyFips ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, countyFips: e.target.value || undefined }))}
                inputMode="numeric"
                maxLength={5}
                placeholder="48201"
                aria-label="County FIPS code"
              />
            </label>
          )}

          <label className="growth-field">
            <span className="growth-field-label">Industry</span>
            <input
              value={form.sector}
              onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value }))}
              placeholder="Plumbing contractors"
              aria-label="Industry"
            />
          </label>

          <label className="growth-field growth-field-narrow">
            <span className="growth-field-label">
              {form.kind === 'brief' ? 'Businesses needed' : 'Per-cycle ceiling'}
            </span>
            <input
              type="number"
              min={1}
              max={500}
              value={form.targetCount}
              onChange={(e) => setForm((f) => ({ ...f, targetCount: Number(e.target.value) }))}
              aria-label="Target count"
            />
          </label>
        </div>

        <p className="growth-field-hint">
          Queueing work commits real spend — a search per phrasing, a geography lookup per
          business, and two model passes per batch of twenty.
        </p>

        <div className="growth-actions">
          <button
            type="button"
            className="growth-primary"
            onClick={() => void submit()}
            disabled={busy === 'create' || !form.sector.trim()}
          >
            {busy === 'create' ? 'Queueing…' : form.kind === 'brief' ? 'Queue brief' : 'Add directive'}
          </button>
        </div>
      </div>

      <WorkItemList
        title="Briefs"
        help="Worked first, in the order they were queued. Cancelling stops a brief that has not finished."
        items={briefs}
        busy={busy}
        onAct={toggle}
        actionLabel={() => 'Cancel'}
      />

      <WorkItemList
        title="Standing directives"
        help="Worked whenever no brief is queued, lowest priority number first. Pausing keeps the history and the counters."
        items={directives}
        busy={busy}
        onAct={toggle}
        actionLabel={(i) => (i.enabled ? 'Pause' : 'Resume')}
      />

      <div className="growth-step">
        <div className="growth-section-head">
          <h3 className="growth-step-title">Exclusion registry</h3>
          <button
            type="button"
            className="portal-btn"
            onClick={() => void loadCompetitors()}
            disabled={busy === 'competitors'}
          >
            {busy === 'competitors' ? 'Loading…' : 'Show competitors'}
          </button>
        </div>
        <p className="growth-step-help">
          Businesses judged to be competitors, data brokers or market-intelligence firms.
          They are never targeted, and the same list is checked at signup.
        </p>
        {competitors && competitors.length === 0 && (
          <p className="growth-empty">Nothing has been excluded yet.</p>
        )}
        {competitors && competitors.length > 0 && (
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th scope="col">Domain</th>
                  <th scope="col">Classification</th>
                  <th scope="col">Why</th>
                  <th scope="col">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c) => (
                  <tr key={c.domain}>
                    <td className="growth-cell-primary">{c.domain}</td>
                    <td>{c.classification.replace(/_/g, ' ')}</td>
                    <td>{c.reason}</td>
                    <td>{c.confidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="growth-stat">
      <span className="growth-stat-value">{value.toLocaleString()}</span>
      <span className="growth-stat-label">{label}</span>
      {hint && <span className="growth-stat-hint">{hint}</span>}
    </div>
  );
}

function WorkItemList({
  title, help, items, busy, onAct, actionLabel,
}: {
  title: string;
  help: string;
  items: DiscoveryWorkItem[];
  busy: string | null;
  onAct: (item: DiscoveryWorkItem) => void | Promise<void>;
  actionLabel: (item: DiscoveryWorkItem) => string;
}) {
  return (
    <div className="growth-step">
      <h3 className="growth-step-title">{title}</h3>
      <p className="growth-step-help">{help}</p>
      {items.length === 0 && <p className="growth-empty">Nothing queued.</p>}
      {items.length > 0 && (
        <div className="growth-table-scroll">
          <table className="growth-table">
            <thead>
              <tr>
                <th scope="col">Where</th>
                <th scope="col">Industry</th>
                <th scope="col">Found</th>
                <th scope="col">Addressable</th>
                <th scope="col">State</th>
                <th scope="col"><span className="portal-visually-hidden">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.itemId}>
                  <td className="growth-cell-primary">
                    {[i.countyFips ?? i.locality, i.regionCode, i.countryCode]
                      .filter(Boolean).join(' · ')}
                  </td>
                  <td>{i.sector}</td>
                  <td>{i.foundCount} of {i.targetCount}</td>
                  {/* ⭐ Census County Business Patterns, not a count of our own records —
                      "how many have we found" cannot tell you whether the search was
                      worth running. An em dash where the figure is genuinely unknown. */}
                  <td>{i.tamEstimate ? i.tamEstimate.toLocaleString() : '—'}</td>
                  <td>
                    {i.kind === 'directive'
                      ? (i.enabled ? 'Running' : 'Paused')
                      : i.status.replace(/_/g, ' ')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="portal-btn"
                      onClick={() => void onAct(i)}
                      disabled={busy === i.itemId
                        || (i.kind === 'brief' && (i.status === 'fulfilled' || i.status === 'cancelled'))}
                    >
                      {actionLabel(i)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
